import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import api, { pollJDGenerationStatus, triggerSemanticMatching, getJdMatchingStatus } from "../../../../api/client";
import { Copy, Check, Users, Plus, Filter, Eye, X, ChevronDown, Edit, Trash2, Save, Upload, ArchiveRestore, Clock, Mic, Loader2, ClipboardCheck } from "lucide-react";

import { CircularProgress, Collapse } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";
import AddDepartmentModal from "../../component/AddDepartmentModal";
import AddJobDescriptionDialog from "../../component/AddJobDescriptionDialog";
import AIQuestionGeneratorModal from "../../component/AIQuestionGeneratorModal";
import QuestionBankPickerModal from "../../component/QuestionBankPickerModal";
import NotificationPopup from "../../component/NotificationPopup";
import ShowDescriptionDialog from "../../component/ShowDescriptionDialog";
import Pagination from "../../component/Pagination";
import ResumeMatchingModal from "./Resumematchingmodal";
import Timeline from "../../component/Timeline";
import JDDraftsModal from "./JDDraftsModal";
import JDFromAudioModal from "./JDFromAudioModal";
import BenchmarksPanel from "./BenchmarksPanel";
import { saveJdBenchmarks, splitJdFormAndBenchmarks, toBenchmarkPayload } from "./benchmarkLabels";
import { convertMarkdownToHtml } from "../../../../utils/markdownToHTML";
import parse from "html-react-parser";
import { IoArchiveOutline } from "react-icons/io5";
import "../../admin.css";

const formatDate = (dateString) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatFullDateTime = (dateString) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const getQuestionTypeOptions = (t) => [
  { value: "concept", label: t('jobDescription.conceptual') },
  { value: "coding", label: t('jobDescription.coding') },
  { value: "mcq", label: t('jobDescription.multipleChoice') },
  { value: "behavioral", label: t('jobDescription.behavioral') },
  { value: "general", label: t('jobDescription.general') },
];

const getQuestionTypeColor = (type) => {
  const colors = {
    concept: "bg-purple-100 text-purple-800",
    coding: "bg-orange-100 text-orange-800",
    mcq: "bg-green-100 text-green-800",
    behavioral: "bg-pink-100 text-pink-800",
    general: "bg-gray-100 text-gray-800",
  };
  return colors[type] || colors.general;
};

const isJdBusy = (jd) => {
  const match = jd.matching_status?.toLowerCase();
  const sched = jd.scheduling_status?.toLowerCase();
  return match === 'processing' || sched === 'processing' || sched === 'scheduling';
};

const getStatusBadge = (status, t) => {
  if (!status) return <span className="text-gray-400">—</span>;

  const statusLower = status.toLowerCase();
  let colorClass = "bg-gray-100 text-gray-800";
  let label = status;

  if (statusLower === 'processing' || statusLower === 'scheduling') {
    colorClass = "bg-blue-100 text-blue-800 animate-pulse";
    label = t(`jobDescription.status${status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}`);
  } else if (statusLower === 'ready' || statusLower === 'completed') {
    colorClass = "bg-green-100 text-green-800";
    label = t(`jobDescription.status${status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}`);
  } else if (statusLower === 'failed') {
    colorClass = "bg-red-100 text-red-800";
    label = t('jobDescription.statusFailed');
  }

  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${colorClass}`}>
      {label}
    </span>
  );
};

const JobDescriptionCard = ({ jobDescription, onAddQuestion, onUpdateQuestions, onGenerateAiQuestion, onAddFromQuestionBank }) => {
  const { t } = useTranslation();
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedQuestion, setEditedQuestion] = useState("");
  const [editedType, setEditedType] = useState("concept");
  const [editedTimeLimit, setEditedTimeLimit] = useState(5);
  const [editedMcqOptions, setEditedMcqOptions] = useState(["", "", "", ""]);
  const [editedMcqCorrectIndex, setEditedMcqCorrectIndex] = useState(0);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const questionTypeOptions = getQuestionTypeOptions(t);

  const handleEdit = (index, question) => {
    setEditingIndex(index);
    setEditedQuestion(question?.nameOfQuestion || question);
    setEditedType(question?.type || "concept");
    setEditedTimeLimit(question?.time_limit || 5);
    if (question?.type === "mcq" && question.choices) {
      setEditedMcqOptions(question.choices.map((c) => c.text || ""));
      const cIdx = question.choices.findIndex((c) => c.is_correct);
      setEditedMcqCorrectIndex(cIdx >= 0 ? cIdx : 0);
    } else {
      setEditedMcqOptions(["", "", "", ""]);
      setEditedMcqCorrectIndex(0);
    }
  };

  const handleSave = async (index) => {
    if (!editedQuestion.trim()) return;
    setIsUpdating(true);
    try {
      const updatedQuestions = [...(jobDescription.questions || [])];
      const existing = updatedQuestions[index];
      updatedQuestions[index] = typeof existing === "object" && existing.nameOfQuestion
        ? {
          ...existing,
          nameOfQuestion: editedQuestion.trim(),
          type: editedType,
          ...(editedType === "coding" && { time_limit: editedTimeLimit > 0 ? editedTimeLimit : 5 }),
          ...(editedType === "mcq" && {
            choices: editedMcqOptions.map((text, i) => ({
              text: text || "",
              is_correct: i === editedMcqCorrectIndex,
            })),
          }),
        }
        : {
          nameOfQuestion: editedQuestion.trim(),
          type: editedType,
          ...(editedType === "coding" && { time_limit: editedTimeLimit > 0 ? editedTimeLimit : 5 }),
          ...(editedType === "mcq" && {
            choices: editedMcqOptions.map((text, i) => ({
              text: text || "",
              is_correct: i === editedMcqCorrectIndex,
            })),
          }),
        };

      await api.put(`job-descriptions/${jobDescription.id}/`, { questions: updatedQuestions });
      onUpdateQuestions(jobDescription.id, updatedQuestions);
      setEditingIndex(null);
      setEditedQuestion("");
      setEditedType("concept");
      setEditedTimeLimit(5);
      setEditedMcqOptions(["", "", "", ""]);
      setEditedMcqCorrectIndex(0);
    } catch (error) {
      alert(t('jobDescription.failedToUpdateQuestion'));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (deleteIndex === null) return;
    setIsUpdating(true);
    try {
      const updatedQuestions = [...(jobDescription.questions || [])];
      updatedQuestions.splice(deleteIndex, 1);
      await api.put(`job-descriptions/${jobDescription.id}/`, { questions: updatedQuestions });
      onUpdateQuestions(jobDescription.id, updatedQuestions);
      setDeleteIndex(null);
    } catch (error) {
      alert(t('jobDescription.failedToDeleteQuestion'));
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 m-4 shadow-sm max-w-full min-w-0">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg text-gray-900 border-b border-blue-200 pb-2">
            {t('jobDescription.questionsFor')} {jobDescription.title}
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onAddFromQuestionBank(jobDescription)}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-blue-200 hover:bg-blue-50 text-blue-700 rounded-lg transition-all shadow-sm font-medium"
              disabled={isUpdating}
            >
              <LibraryBooksOutlinedIcon sx={{ fontSize: 16 }} /> {t('questionBank.addFromBank', 'Add from Question Bank')}
            </button>
            <button
              onClick={() => onGenerateAiQuestion(jobDescription)}
              className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg transition-all shadow-md hover:shadow-lg font-medium"
              disabled={isUpdating}
            >
              <AutoAwesomeOutlinedIcon sx={{ fontSize: 16 }} /> {t('jobDescription.generateWithAI')}
            </button>
            <button
              onClick={() => onAddQuestion(jobDescription.id)}
              className="flex items-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors shadow-md hover:shadow-lg font-medium"
              disabled={isUpdating}
            >
              <Plus size={14} /> {t('jobDescription.addQuestion')}
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-600">{t('jobDescription.interviewQuestions')}</span>
            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
              {jobDescription.questions?.length || 0}
            </span>
          </div>
          {jobDescription.questions?.length > 0 ? (
            <div className="space-y-3">
              {jobDescription.questions.map((question, index) => {
                const questionText = question?.nameOfQuestion || question;
                const questionType = question?.type || "general";

                return (
                  <div key={index} className="bg-gray-50 p-3 rounded border-l-3 border-blue-400">
                    <div className="flex items-start gap-3">
                      <div className="flex flex-col gap-1 shrink-0">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full">Q{index + 1}</span>
                        <div className="flex items-center gap-1">
                          <span className={`px-2 py-1 rounded-full ${getQuestionTypeColor(questionType)}`}>
                            {questionTypeOptions.find(opt => opt.value === questionType)?.label || questionType}
                          </span>
                          {questionType === "coding" && (
                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-[10px] font-semibold text-center whitespace-nowrap">
                              {question?.time_limit || 5} min
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex-1">
                        {editingIndex === index ? (
                          <div className="space-y-2">
                            <textarea
                              value={editedQuestion}
                              onChange={(e) => setEditedQuestion(e.target.value)}
                              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                              rows="2"
                              disabled={isUpdating}
                            />
                            <select
                              value={editedType}
                              onChange={(e) => setEditedType(e.target.value)}
                              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                              disabled={isUpdating}
                            >
                              {questionTypeOptions.map((type) => (
                                <option key={type.value} value={type.value}>{type.label}</option>
                              ))}
                            </select>
                            {editedType === "coding" && (
                              <div className="flex flex-col gap-1">
                                <label className="text-xs text-gray-500">{t('jobDescription.timeLimit', 'Time Limit (minutes)')}</label>
                                <input
                                  type="number"
                                  value={editedTimeLimit}
                                  onChange={(e) => setEditedTimeLimit(Math.min(60, Math.max(1, parseInt(e.target.value) || 5)))}
                                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                                  min="1"
                                  max="60"
                                  disabled={isUpdating}
                                />
                                <span className="text-[10px] text-gray-400">{t('jobDescription.timeLimitHelper', 'Default: 5 min (Max: 60)')}</span>
                              </div>
                            )}
                            {editedType === "mcq" && (
                              <div className="space-y-2 p-3 bg-white rounded-lg border border-gray-200">
                                <div className="text-xs font-semibold text-gray-700">{t('jobDescription.multipleChoiceOptions', 'Multiple Choice Options')}</div>
                                <div className="grid grid-cols-1 gap-2">
                                  {editedMcqOptions.map((opt, i) => (
                                    <div key={i} className="flex items-center gap-2">
                                      <input
                                        type="radio"
                                        name={`edit-mcq-correct-${index}`}
                                        checked={editedMcqCorrectIndex === i}
                                        onChange={() => setEditedMcqCorrectIndex(i)}
                                        disabled={isUpdating}
                                      />
                                      <input
                                        type="text"
                                        value={opt}
                                        onChange={(e) => setEditedMcqOptions(prev => prev.map((v, idx) => idx === i ? e.target.value : v))}
                                        className="flex-1 p-1.5 border border-gray-300 rounded text-sm outline-none focus:ring-1 focus:ring-blue-500"
                                        placeholder={`${t('jobDescription.answer', 'Answer')} ${String.fromCharCode(65 + i)}`}
                                        disabled={isUpdating}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleSave(index)}
                                className="flex items-center gap-1 px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                                disabled={isUpdating || !editedQuestion.trim() || (editedType === "mcq" && editedMcqOptions.some(o => !o.trim()))}
                              >
                                <Save size={12} /> {t('common.save')}
                              </button>
                              <button
                                onClick={() => {
                                  setEditingIndex(null);
                                  setEditedQuestion("");
                                  setEditedType("concept");
                                  setEditedTimeLimit(5);
                                  setEditedMcqOptions(["", "", "", ""]);
                                  setEditedMcqCorrectIndex(0);
                                }}
                                className="flex items-center gap-1 px-2 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded transition-colors"
                                disabled={isUpdating}
                              >
                                <X size={12} /> {t('jobDescription.cancel')}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex-1">
                            <div className="flex items-start justify-between">
                              <span className="text-gray-700 flex-1 mr-2">{questionText}</span>
                              <div className="flex gap-1 shrink-0">
                                <button
                                  onClick={() => handleEdit(index, question)}
                                  className="p-1 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                                  title={t('jobDescription.editQuestion')}
                                  disabled={isUpdating}
                                >
                                  <Edit size={14} />
                                </button>
                                <button
                                  onClick={() => setDeleteIndex(index)}
                                  className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
                                  title={t('jobDescription.deleteQuestionTitle')}
                                  disabled={isUpdating}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                            {questionType === "mcq" && Array.isArray(question.choices) && question.choices.length > 0 && (
                              <div className="mt-2 ml-2 space-y-1">
                                {question.choices.map((choice, cIdx) => (
                                  <div key={cIdx} className="flex items-center gap-2 text-sm">
                                    <span className="text-gray-600 w-5">{String.fromCharCode(65 + cIdx)}.</span>
                                    <span className="text-gray-700 flex-1">{choice.text}</span>
                                    {choice.is_correct && (
                                      <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs">{t('jobDescription.correct')}</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6">
              <div className="text-gray-500 mb-3">{t('jobDescription.noQuestionsAdded')}</div>
              <button
                onClick={() => onAddQuestion(jobDescription.id)}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors mx-auto"
                disabled={isUpdating}
              >
                <Plus size={14} /> {t('jobDescription.addFirstQuestion')}
              </button>
            </div>
          )}
        </div>
      </div>

      {deleteIndex !== null && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[999999] animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl w-full max-w-sm shadow-2xl transform transition-all animate-in zoom-in-95 duration-200">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                <Trash2 className="text-red-600" size={24} />
              </div>
              <h3 className="text-lg text-gray-900 mb-2 text-center">{t('jobDescription.deleteQuestion')}</h3>
              <p className="text-gray-600 mb-6 text-center">{t('jobDescription.deleteQuestionMessage')}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteIndex(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={isUpdating}
                >
                  {t('jobDescription.cancel')}
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  disabled={isUpdating}
                >
                  {isUpdating ? t('jobDescription.deleting') : t('jobDescription.delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {jobDescription?.jd_type !== 'practice' && !jobDescription?.is_practice && (
        <BenchmarksPanel jobDescription={jobDescription} />
      )}
    </div>
  );
};

export default function JobDescription({ onLogout }) {
  const { t, i18n } = useTranslation();
  const isRTL = document.documentElement.dir === 'rtl' || i18n.language === 'ar';
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);
  const currentRequestRef = useRef(null);
  const loadedBenchmarksRef = useRef("[]");
  const questionTypeOptions = getQuestionTypeOptions(t);

  const initialFormState = {
    title: "", department: "", status: "open", employment_type: "", experience_level: "",
    required_skills: [], nice_to_have_skills: [], salary_range_min: "", salary_range_max: "",
    description: "", is_public: false, interview_style: "default", campus_questions_per_category: 3,
    questions: [], work_type: "",
    benchmarks: [], benchmarks_approve: false,
  };

  const [showJobDescriptionModal, setShowJobDescriptionModal] = useState(false);
  const [jobDescriptions, setJobDescriptions] = useState([]);
  const [selectedJobDescription, setSelectedJobDescription] = useState(null);
  const [jobDescriptionForm, setJobDescriptionForm] = useState(initialFormState);
  const [jobDescriptionError, setJobDescriptionError] = useState("");
  const [departments, setDepartments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [showAddDepartmentModal, setShowAddDepartmentModal] = useState(false);
  const [newDepartmentName, setNewDepartmentName] = useState("");
  const [departmentError, setDepartmentError] = useState("");
  const [departmentSuccess, setDepartmentSuccess] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [description, setDescription] = useState("");
  const [selectedJobTitle, setSelectedJobTitle] = useState("");
  const [openDescriptionDialog, setOpenDescriptionDialog] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
  const [selectedJobForQuestion, setSelectedJobForQuestion] = useState(null);
  const [globalQuestionText, setGlobalQuestionText] = useState("");
  const [globalQuestionType, setGlobalQuestionType] = useState("concept");
  const [uploadingJD, setUploadingJD] = useState(false);
  const [globalMcqOptions, setGlobalMcqOptions] = useState(["", "", "", ""]);
  const [globalMcqCorrectIndex, setGlobalMcqCorrectIndex] = useState(0);
  const [globalTimeLimit, setGlobalTimeLimit] = useState(5);
  const [showResumeMatchingModal, setShowResumeMatchingModal] = useState(false);
  const [selectedJobForMatching, setSelectedJobForMatching] = useState(null);
  const [showAiGeneratorInCard, setShowAiGeneratorInCard] = useState(false);
  const [selectedJobForAiGenerator, setSelectedJobForAiGenerator] = useState(null);
  const [showQuestionBankPicker, setShowQuestionBankPicker] = useState(false);
  const [selectedJobForBank, setSelectedJobForBank] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [selectedJobIdForTimeline, setSelectedJobIdForTimeline] = useState(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showTogglePublicConfirm, setShowTogglePublicConfirm] = useState(false);
  const [jobToToggle, setJobToToggle] = useState(null);
  const [showAudioModal, setShowAudioModal] = useState(false);
  const [showDraftsModal, setShowDraftsModal] = useState(false);
  const [notificationAuditId, setNotificationAuditId] = useState(null);

  const initialPage = parseInt(localStorage.getItem("jobPage")) || 1;
  const initialPageSize = parseInt(localStorage.getItem("jobPageSize")) || 10;

  const [paginationData, setPaginationData] = useState({
    current_page: initialPage, page_size: initialPageSize, total_count: 0, total_pages: 1,
    has_next: false, has_previous: false, next_page: null, previous_page: null,
  });

  const [filters, setFilters] = useState({
    search: "", jd_type: "", company: "", department: "", is_public: "",
    created_date: "", status: "open", ordering: "-created_at",
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const clearReviewAuditQuery = useCallback(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has("review_audit_id")) {
      return;
    }

    params.delete("review_audit_id");
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : "",
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  const fetchJobDescriptions = useCallback(async (page = paginationData.current_page, pageSize = paginationData.page_size, options = {}) => {
    const silent = options?.silent === true;
    if (!silent) setLoading(true);
    const controller = new AbortController();
    currentRequestRef.current = controller;

    try {
      const params = new URLSearchParams({ page: page.toString(), page_size: pageSize.toString() });
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== "" && value !== null) params.append(key, value);
      });

      const res = await api.get(`job-descriptions/list_with_pagination/?${params.toString()}`, {
        headers: { Authorization: `${localStorage.getItem("adminToken")}` },
        signal: controller.signal,
      });

      const { results, pagination } = res.data;
      setJobDescriptions(Array.isArray(results) ? results : []);
      setPaginationData({
        current_page: pagination?.current_page || page,
        page_size: pagination?.page_size || pageSize,
        total_count: pagination?.total_count || 0,
        total_pages: pagination?.total_pages || 1,
        has_next: pagination?.has_next || false,
        has_previous: pagination?.has_previous || false,
        next_page: pagination?.next_page || null,
        previous_page: pagination?.previous_page || null,
      });

      localStorage.setItem("jobPage", pagination?.current_page || page);
      localStorage.setItem("jobPageSize", pagination?.page_size || pageSize);
    } catch (err) {
      // Ignore canceled requests (both AbortError and Axios CanceledError)
      if (err.name === "AbortError" || err.name === "CanceledError" || err.code === "ERR_CANCELED") {
        return;
      }
      console.error("Error fetching job descriptions:", err);
      setJobDescriptions([]);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [filters, paginationData.current_page, paginationData.page_size]);

  useEffect(() => {
    api.get("departments/").then(res => setDepartments(Array.isArray(res.data) ? res.data : res.data.results || [])).catch(() => setDepartments([]));
    api.get("companies/").then(res => setCompanies(Array.isArray(res.data) ? res.data : res.data.results || [])).catch(() => setCompanies([]));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reviewAuditId = params.get("review_audit_id");

    if (!reviewAuditId) {
      setNotificationAuditId(null);
      return;
    }

    setNotificationAuditId(reviewAuditId);
    setShowDraftsModal(false);
    setShowAudioModal(true);
  }, [location.search]);

  useEffect(() => {
    if (currentRequestRef.current) currentRequestRef.current.abort();
    const timer = setTimeout(() => fetchJobDescriptions(), 300);
    return () => {
      clearTimeout(timer);
      if (currentRequestRef.current) currentRequestRef.current.abort();
    };
  },// eslint-disable-next-line react-hooks/exhaustive-deps
    [filters, paginationData.current_page, paginationData.page_size]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
    setPaginationData(prev => ({ ...prev, current_page: 1 }));
  };

  const clearAllFilters = () => {
    setFilters({ search: "", jd_type: "", company: "", department: "", is_public: "", created_date: "", status: "open", ordering: "-created_at" });
    setPaginationData(prev => ({ ...prev, current_page: 1 }));
  };

  const hasActiveFilters = () => {
    return filters.search || filters.jd_type || filters.company || filters.department ||
      filters.is_public !== "" || filters.created_date || (filters.status && filters.status !== "open");
  };

  const handleJobDescriptionSubmit = async (e) => {
    e.preventDefault();
    setJobDescriptionError("");
    const { jdPayload, benchmarks, approve } = splitJdFormAndBenchmarks(jobDescriptionForm);
    try {
      let jdId = selectedJobDescription?.id;
      if (selectedJobDescription) {
        await api.patch(`job-descriptions/${selectedJobDescription.id}/`, jdPayload);
        showSnackbar(t('jobDescription.jobDescriptionUpdated'));
      } else {
        const created = await api.post("job-descriptions/", jdPayload);
        jdId = created.data?.id;
        showSnackbar(t('jobDescription.jobDescriptionAdded'));
      }
      if (jdId && benchmarks.length) {
        const current = JSON.stringify(toBenchmarkPayload(benchmarks));
        const changed = current !== loadedBenchmarksRef.current;
        if (changed || approve || !selectedJobDescription) {
          try {
            await saveJdBenchmarks(api, jdId, benchmarks, { approve });
          } catch (benchErr) {
            const detail = benchErr.response?.data?.error;
            showSnackbar(
              typeof detail === 'string'
                ? detail
                : t('jobDescription.scorecardSaveFailed', 'Job saved, but the interview benchmarks could not be saved. Open the job to finish it.'),
              "error"
            );
          }
        }
      }
      setShowJobDescriptionModal(false);
      setSelectedJobDescription(null);
      setJobDescriptionForm(initialFormState);
      fetchJobDescriptions();
    } catch (err) {
      showSnackbar(selectedJobDescription ? t('jobDescription.failedToUpdateJD') : t('jobDescription.failedToAddJD'), "error");
    }
  };

  const handleArchive = async () => {
    try {
      await api.post(`job-descriptions/${deleteId}/archive/`);
      fetchJobDescriptions();
      setDialogOpen(false);
      setDeleteId(null);
      showSnackbar(t('jobDescription.jobDescriptionArchived'));
    } catch (err) {
      console.error("Error archiving job description:", err);
      showSnackbar(t('jobDescription.failedToArchiveJD'), "error");
    }
  };

  const handleUnarchive = async (jobId) => {
    try {
      await api.post(`job-descriptions/${jobId}/unarchive/`);
      fetchJobDescriptions();
      showSnackbar(t('jobDescription.jobDescriptionUnarchived'));
    } catch (err) {
      console.error("Error unarchiving job description:", err);
      showSnackbar(t('jobDescription.failedToUnarchiveJD'), "error");
    }
  };

  const openEditJobDescription = async (jobDescription) => {
    setSelectedJobDescription(jobDescription);
    const form = {
      title: jobDescription.title || "",
      department: jobDescription.department || "",
      status: jobDescription.status || "open",
      employment_type: jobDescription.employment_type || "",
      experience_level: jobDescription.experience_level || "",
      required_skills: jobDescription.required_skills || [],
      nice_to_have_skills: jobDescription.nice_to_have_skills || [],
      salary_range_min: jobDescription.salary_range_min || "",
      salary_range_max: jobDescription.salary_range_max || "",
      description: jobDescription.description || "",
      is_public: jobDescription.is_public || false,
      interview_style: jobDescription.interview_style || "default",
      campus_questions_per_category: jobDescription.campus_questions_per_category || 3,
      questions: jobDescription.questions || [],
      work_type: jobDescription.work_type || "",
      benchmarks: [],
      benchmarks_approve: false,
    };
    setJobDescriptionForm(form);
    setShowJobDescriptionModal(true);
    loadedBenchmarksRef.current = "[]";
    if (jobDescription?.id && jobDescription.jd_type !== 'practice' && !jobDescription.is_practice) {
      try {
        const res = await api.get(`job-descriptions/${jobDescription.id}/benchmarks/`);
        const loaded = res.data.benchmarks || [];
        loadedBenchmarksRef.current = JSON.stringify(toBenchmarkPayload(loaded));
        setJobDescriptionForm((prev) => ({
          ...prev,
          benchmarks: loaded,
          benchmarks_approve: false,
        }));
      } catch (err) {
        console.error("Could not load interview benchmarks for edit", err);
      }
    }
  };

  const handleUploadJD = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'];
    if (!allowedTypes.includes(file.type)) {
      showSnackbar(t('jobDescription.invalidFileType'), "error");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      showSnackbar(t('jobDescription.fileSizeExceeded'), "error");
      return;
    }

    setUploadingJD(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('job-descriptions/parse-from-file/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success) {
        let jobDescriptionText = response.data.description || "";
        if (response.data.title) {
          const titleRegex = new RegExp(`^#{1,3}\\s*${response.data.title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*`, "gm");
          jobDescriptionText = jobDescriptionText.replace(titleRegex, "");
        }
        const htmlContent = convertMarkdownToHtml(jobDescriptionText);

        setJobDescriptionForm({
          title: response.data.title || "",
          department: "",
          status: response.data.status || "open",
          employment_type: response.data.employment_type || "",
          experience_level: response.data.experience_level || "",
          required_skills: response.data.required_skills || [],
          nice_to_have_skills: response.data.nice_to_have_skills || [],
          salary_range_min: response.data.salary_range_min || "",
          salary_range_max: response.data.salary_range_max || "",
          description: htmlContent,
          is_public: false,
          interview_style: "default",
          campus_questions_per_category: 3,
          questions: [],
          work_type: response.data.work_type || "",
        });

        setSelectedJobDescription(null);
        setShowJobDescriptionModal(true);
        showSnackbar(t('jobDescription.jdExtractedSuccess'));
      } else {
        throw new Error('Failed to extract job description');
      }
    } catch (err) {
      showSnackbar(err.response?.data?.error || t('jobDescription.failedToParseJD'), "error");
    } finally {
      setUploadingJD(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAddDepartment = async (e) => {
    e.preventDefault();
    try {
      const companyId = departments.length > 0 ? departments[0].company : 1;
      const res = await api.post("departments/", { name: newDepartmentName, company: companyId });
      setDepartments(prev => [...(Array.isArray(prev) ? prev : []), res.data]);
      showSnackbar(t('jobDescription.departmentAdded'));
      setShowAddDepartmentModal(false);
      setNewDepartmentName("");
    } catch (err) {
      showSnackbar(t('jobDescription.failedToAddDepartment'), "error");
    }
  };

  const handleCopyPublicLink = async (jobDescription) => {
    if (!jobDescription.is_public || !jobDescription.public_link) {
      showSnackbar(t('jobDescription.notPublicOrNoLink'), "warning");
      return;
    }

    try {
      await navigator.clipboard.writeText(jobDescription.public_link);
      setCopiedId(jobDescription.id);
      showSnackbar(t('jobDescription.publicLinkCopied'));
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      showSnackbar(t('jobDescription.failedToCopyLink'), "error");
    }
  };

  const toggleRowExpansion = (id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      const opening = !next.has(id);
      if (opening) next.add(id);
      else next.delete(id);
      if (opening) {
        window.setTimeout(() => {
          document.getElementById(`jd-benchmarks-${id}`)?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        }, 50);
      }
      return next;
    });
  };

  const handleAddQuestion = (jobId) => {
    const job = jobDescriptions.find(j => j.id === jobId);
    setSelectedJobForQuestion(job);
    setShowAddQuestionModal(true);
    setGlobalQuestionText("");
    setGlobalQuestionType("concept");
    setGlobalMcqOptions(["", "", "", ""]);
    setGlobalMcqCorrectIndex(0);
    setGlobalTimeLimit(5);
  };

  const handleUpdateQuestions = (jobId, updatedQuestions) => {
    setJobDescriptions(prevJobs => prevJobs.map(job => job.id === jobId ? { ...job, questions: updatedQuestions } : job));
  };

  const handleGenerateAiQuestion = (job) => {
    setSelectedJobForAiGenerator(job);
    setShowAiGeneratorInCard(true);
  };

  const handleOpenQuestionBank = (job) => {
    setSelectedJobForBank(job);
    setShowQuestionBankPicker(true);
  };

  const handleQuestionBankAdded = async (newQuestions) => {
    const targetJob = selectedJobForBank || selectedJobForQuestion;
    if (!targetJob) return;

    try {
      const updatedQuestions = [...(targetJob.questions || []), ...newQuestions];
      await api.put(`job-descriptions/${targetJob.id}/`, { questions: updatedQuestions });
      handleUpdateQuestions(targetJob.id, updatedQuestions);
      setShowQuestionBankPicker(false);
      setSelectedJobForBank(null);
      setShowAddQuestionModal(false);
      setSelectedJobForQuestion(null);
      showSnackbar(t('jobDescription.questionAdded'));
    } catch (error) {
      showSnackbar(t('jobDescription.failedToAddQuestion'), "error");
    }
  };

  const handleAiQuestionsAddedInCard = async (newQuestions) => {
    if (!selectedJobForAiGenerator) return;

    try {
      const updatedQuestions = [...(selectedJobForAiGenerator.questions || []), ...newQuestions];
      await api.put(`job-descriptions/${selectedJobForAiGenerator.id}/`, { questions: updatedQuestions });
      handleUpdateQuestions(selectedJobForAiGenerator.id, updatedQuestions);
      setShowAiGeneratorInCard(false);
      setSelectedJobForAiGenerator(null);
      showSnackbar(t('jobDescription.questionAdded'));
    } catch (error) {
      showSnackbar(t('jobDescription.failedToAddQuestion'), "error");
    }
  };

  const handleOpenResumeMatching = (job) => {
    setSelectedJobForMatching(job);
    setShowResumeMatchingModal(true);
  };

  const handleStartResumeMatching = async (topN) => {
    try {
      const res = await triggerSemanticMatching(selectedJobForMatching.id);

      showSnackbar(t('jobDescription.resumeMatchingStarted'));
      setShowResumeMatchingModal(false);
      const jobId = selectedJobForMatching.id;
      setSelectedJobForMatching(null);
      navigate(`/admin/matching-resumes/${jobId}/${res.task_id}?limit=${topN}`);
    } catch (error) {
      showSnackbar(t('jobDescription.failedToStartMatching'), "error");
    }
  };

  const pollJdStatuses = useCallback(async (ids) => {
    const updates = await Promise.all(ids.map(async (id) => {
      try {
        return await getJdMatchingStatus(id);
      } catch (err) {
        console.error(`Error polling JD ${id} status:`, err);
        return null;
      }
    }));

    const byId = new Map(
      updates.filter((update) => update && update.id != null).map((update) => [update.id, update])
    );
    if (byId.size === 0) return;

    setJobDescriptions((prev) => {
      let changed = false;
      const next = prev.map((jd) => {
        const update = byId.get(jd.id);
        if (!update) return jd;
        if (
          jd.matching_status === update.matching_status &&
          jd.scheduling_status === update.scheduling_status &&
          jd.processed_items_count === update.processed_items_count &&
          jd.total_items_count === update.total_items_count
        ) {
          return jd;
        }
        changed = true;
        return {
          ...jd,
          matching_status: update.matching_status,
          scheduling_status: update.scheduling_status,
          processed_items_count: update.processed_items_count,
          total_items_count: update.total_items_count,
        };
      });
      return changed ? next : prev;
    });
  }, []);

  const processingKey = useMemo(
    () => jobDescriptions.filter(isJdBusy).map((jd) => jd.id).sort((a, b) => a - b).join(','),
    [jobDescriptions]
  );

  useEffect(() => {
    if (!processingKey) return undefined;
    const ids = processingKey.split(',').map(Number);

    pollJdStatuses(ids);
    const interval = setInterval(() => {
      pollJdStatuses(ids);
    }, 5000);

    return () => clearInterval(interval);
  }, [processingKey, pollJdStatuses]);

  const handlePageChange = (newPage) => {
    setPaginationData(prev => ({ ...prev, current_page: newPage }));
    localStorage.setItem("jobPage", newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPaginationData(prev => ({ ...prev, page_size: newSize, current_page: 1 }));
    localStorage.setItem("jobPage", 1);
    localStorage.setItem("jobPageSize", newSize);
  };

  const truncateUrl = (url, maxLength = 50) => {
    if (!url || url.length <= maxLength) return url || "";
    return url.substring(0, maxLength) + "...";
  };

  const truncateTitle = (title, maxLength = 50) => {
    if (!title || title.length <= maxLength) return title || "";
    return title.substring(0, maxLength) + "...";
  };

  const handleAddGlobalQuestion = async () => {
    const question = globalQuestionText.trim();
    if (!question) return;

    try {
      const newQuestion = {
        nameOfQuestion: question,
        type: globalQuestionType,
        ...(globalQuestionType === "coding" && { time_limit: globalTimeLimit > 0 ? globalTimeLimit : 5 })
      };

      if (globalQuestionType === "mcq") {
        const trimmed = globalMcqOptions.map(s => s.trim());
        if (trimmed.some(s => !s) || globalMcqCorrectIndex < 0 || globalMcqCorrectIndex > 3) return;
        newQuestion.choices = trimmed.map((text, i) => ({ text, is_correct: i === globalMcqCorrectIndex }));
      }

      const updatedQuestions = [...(selectedJobForQuestion.questions || []), newQuestion];
      await api.put(`job-descriptions/${selectedJobForQuestion.id}/`, { questions: updatedQuestions });
      handleUpdateQuestions(selectedJobForQuestion.id, updatedQuestions);
      setShowAddQuestionModal(false);
      setSelectedJobForQuestion(null);
      setGlobalQuestionText("");
      setGlobalQuestionType("concept");
      setGlobalTimeLimit(5);
      setGlobalMcqOptions(["", "", "", ""]);
      setGlobalMcqCorrectIndex(0);
      showSnackbar(t('jobDescription.questionAdded'));
    } catch (error) {
      showSnackbar(t('jobDescription.failedToAddQuestion'), "error");
    }
  };

  const handleTogglePublicClick = (jobDescription) => {
    setJobToToggle(jobDescription);
    setShowTogglePublicConfirm(true);
  };

  const confirmTogglePublic = async () => {
    if (!jobToToggle) return;

    try {
      const newIsPublic = !jobToToggle.is_public;
      const response = await api.patch(`job-descriptions/${jobToToggle.id}/`, { is_public: newIsPublic });

      // Update state with the full response data including public_link
      const updatedJobData = response.data?.data || {};
      setJobDescriptions(prevJobs =>
        prevJobs.map(job =>
          job.id === jobToToggle.id
            ? {
              ...job,
              is_public: updatedJobData.is_public ?? newIsPublic,
              public_link: updatedJobData.public_link ?? job.public_link,
              jd_type: updatedJobData.jd_type ?? job.jd_type
            }
            : job
        )
      );
      showSnackbar(
        newIsPublic
          ? t('jobDescription.jobMadePublic')
          : t('jobDescription.jobMadePrivate')
      );
    } catch (error) {
      showSnackbar(t('jobDescription.statusUpdateFailed'), "error");
    } finally {
      setShowTogglePublicConfirm(false);
      setJobToToggle(null);
    }
  };

  const handleAudioJDApply = (data) => {
    if (!data) return;

    setJobDescriptionForm({
      ...initialFormState,
      title: data.title || "",
      description: data.description || "",
      employment_type: data.employment_type || "",
      experience_level: data.experience_level || "",
      required_skills: data.required_skills || data.required_skills_list || [],
      nice_to_have_skills: data.nice_to_have_skills || data.nice_to_have_skills_list || [],
      questions: data.questions || data.interview_questions || [],
      work_type: data.work_type || "on_site",
      salary_range_min: data.salary_range_min || "",
      salary_range_max: data.salary_range_max || "",
    });

    setSelectedJobDescription(null);
    setShowAudioModal(false);
    setNotificationAuditId(null);
    clearReviewAuditQuery();
    setShowJobDescriptionModal(true);
  };

  const handleCloseAudioModal = () => {
    setShowAudioModal(false);

    if (!notificationAuditId) {
      return;
    }

    setNotificationAuditId(null);
    clearReviewAuditQuery();
  };

  const handleSelectDraft = async (draftId) => {
    try {
      setLoading(true);
      const res = await pollJDGenerationStatus(draftId);

      if (res.status === 'done' && res.structured_description) {
        const sd = res.structured_description;

        // Convert markdown to HTML for the description field, excluding the title
        let rawDescription = res.generated_description || '';
        if (sd.title) {
          const titleRegex = new RegExp(`^#{1,3}\\s*${sd.title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*`, 'gm');
          rawDescription = rawDescription.replace(titleRegex, '');
        }
        const htmlDescription = convertMarkdownToHtml(rawDescription);

        setJobDescriptionForm({
          ...initialFormState,
          title: sd.title || "",
          description: htmlDescription,
          employment_type: sd.employment_type || "",
          experience_level: sd.experience_level || "",
          required_skills: sd.required_skills_list || sd.required_skills || [],
          nice_to_have_skills: sd.nice_to_have_skills_list || sd.nice_to_have_skills || [],
          questions: sd.interview_questions || sd.questions || [],
          work_type: sd.work_type || "on_site",
          salary_range_min: sd.salary_range_min || "",
          salary_range_max: sd.salary_range_max || "",
        });

        setSelectedJobDescription(null);
        setShowDraftsModal(false);
        setShowJobDescriptionModal(true);
      } else {
        showSnackbar(t('jobDescription.draftNotReady', 'Draft is not ready yet or failed.'), "error");
      }
    } catch (error) {
      console.error('Error fetching draft:', error);
      showSnackbar(t('jobDescription.failedToLoadDraft', 'Failed to load draft details.'), "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30" style={{ paddingTop: "10px" }}>
      <NotificationPopup open={snackbar.open} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} message={snackbar.message} />

      <div className="w-full max-w-[1920px] mx-auto px-4 py-3 sm:px-6 lg:px-8 h-full">
        <div className="bg-white rounded-xl flex flex-col">
          {/* Compact Header */}
          <div className="px-4 py-3 text-white rounded-t-xl shadow-sm" style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h1 className="text-lg font-semibold mb-0.5 text-white">{t('jobDescription.title', 'Job Descriptions')}</h1>
                <p className="text-xs text-blue-100">{t('jobDescription.pageSubtitle', 'Manage job descriptions for open positions')}</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => { setSelectedJobDescription(null); setJobDescriptionForm(initialFormState); setShowJobDescriptionModal(true); }}
                  className="flex items-center gap-1.5 bg-white text-blue-600 px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors shadow-sm text-sm font-medium"
                  title={t('jobDescription.addJobDescription', 'Add Job Description')}
                >
                  <Plus size={14} />
                  {t('jobDescription.addJobDescription', 'Add Job Description')}
                </button>
                <input type="file" ref={fileInputRef} accept=".pdf,.doc,.docx" onChange={handleUploadJD} className="hidden" id="jd-file-upload" />
                <label
                  htmlFor="jd-file-upload"
                  className={`flex items-center gap-1.5 bg-white text-green-600 px-3 py-1.5 rounded-lg hover:bg-green-50 transition-colors shadow-sm cursor-pointer text-sm font-medium ${uploadingJD ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title={t('jobDescription.uploadJDFromFile', 'Upload JD from File')}
                >
                  {uploadingJD ? <CircularProgress size={14} color="inherit" /> : <Upload size={14} />}
                  {t('jobDescription.uploadJD', 'Upload JD')}
                </label>
                <button
                  onClick={() => setShowAudioModal(true)}
                  className="flex items-center gap-1.5 bg-white text-purple-600 px-3 py-1.5 rounded-lg hover:bg-purple-50 transition-colors shadow-sm text-sm font-medium"
                  title={t('jobDescription.generateFromAudio', 'Generate JD from Audio')}
                >
                  <Mic size={14} />
                  {t('jobDescription.audioJD', 'Audio JD')}
                </button>
                <button
                  onClick={() => setShowDraftsModal(true)}
                  className="flex items-center gap-1.5 bg-white text-indigo-600 px-3 py-1.5 rounded-lg hover:bg-indigo-50 transition-colors shadow-sm text-sm font-medium"
                  title={t('jobDescription.viewDrafts', 'View Saved Drafts')}
                >
                  <Clock size={14} />
                  {t('jobDescription.drafts', 'Drafts')}
                </button>
              </div>
            </div>
          </div>

          {/* Advanced Filters */}
          <div className="px-4 py-3 border-b bg-gradient-to-r from-gray-50 to-blue-50/30">
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                <div className="flex items-center gap-2 flex-1 w-full">
                  <div className="relative flex-1">
                    <Filter size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={filters.search}
                      onChange={(e) => handleFilterChange("search", e.target.value)}
                      placeholder={t('jobDescription.searchPlaceholder')}
                      className="w-full pl-10 pr-4 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white shadow-sm text-sm font-medium"
                    />
                  </div>
                  {hasActiveFilters() && (
                    <button onClick={clearAllFilters} className="flex items-center gap-1.5 px-3 py-1.5 text-red-600 hover:text-white bg-white border border-red-300 rounded-lg hover:bg-red-500 transition-all duration-200 text-sm font-medium shadow-sm whitespace-nowrap">
                      {t('jobDescription.clearAll')}
                    </button>
                  )}
                </div>
                <button
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-lg transition-all duration-200 text-sm font-medium shadow-sm ${showAdvancedFilters ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white border-blue-600 shadow-md" : "bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:text-blue-600"}`}
                >
                  <Filter size={14} /> {t('jobDescription.advancedFilters')}
                  <ChevronDown size={14} className={`transition-transform duration-200 ${showAdvancedFilters ? "rotate-180" : ""}`} />
                </button>
              </div>

              {showAdvancedFilters && (
                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
                    <div>
                      <label className="block text-gray-600 mb-1 font-medium text-xs">{t('jobDescription.jdType')}</label>
                      <select value={filters.jd_type} onChange={(e) => handleFilterChange("jd_type", e.target.value)} className="w-full px-3 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white shadow-sm text-sm font-medium">
                        <option value="">{t('jobDescription.allTypes')}</option>
                        <option value="public">{t('jobDescription.public')}</option>
                        <option value="private">{t('jobDescription.private')}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-600 mb-1 font-medium text-xs">{t('jobDescription.status')}</label>
                      <select value={filters.status} onChange={(e) => handleFilterChange("status", e.target.value)} className="w-full px-3 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white shadow-sm text-sm font-medium">
                        <option value="open">{t('jobDescription.open')}</option>
                        <option value="closed">{t('jobDescription.closed')}</option>
                        <option value="cancelled">{t('jobDescription.cancelled')}</option>
                        <option value="expired">{t('jobDescription.expired')}</option>
                        <option value="archived">{t('jobDescription.archived')}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-600 mb-1 font-medium text-xs">{t('jobDescription.company')}</label>
                      <select value={filters.company} onChange={(e) => handleFilterChange("company", e.target.value)} className="w-full px-3 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white shadow-sm text-sm font-medium">
                        <option value="">{t('jobDescription.allCompanies')}</option>
                        {companies.map((company) => (<option key={company.id} value={company.id}>{company.name}</option>))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-600 mb-1 font-medium text-xs">{t('jobDescription.department')}</label>
                      <select value={filters.department} onChange={(e) => handleFilterChange("department", e.target.value)} className="w-full px-3 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white shadow-sm text-sm font-medium">
                        <option value="">{t('jobDescription.allDepartments')}</option>
                        {departments.map((dept) => (<option key={dept.id} value={dept.id}>{dept.name}</option>))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-600 mb-1 font-medium text-xs">{t('jobDescription.createdDate')}</label>
                      <input type="date" value={filters.created_date} onChange={(e) => handleFilterChange("created_date", e.target.value)} className="w-full px-3 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white shadow-sm text-sm font-medium" />
                    </div>
                    <div>
                      <label className="block text-gray-600 mb-1 font-medium text-xs">{t('jobDescription.sortBy')}</label>
                      <select value={filters.ordering} onChange={(e) => handleFilterChange("ordering", e.target.value)} className="w-full px-3 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white shadow-sm text-sm font-medium">
                        <option value="-created_at">{t('jobDescription.newestFirst')}</option>
                        <option value="created_at">{t('jobDescription.oldestFirst')}</option>
                        <option value="title">{t('jobDescription.titleAZ')}</option>
                        <option value="-title">{t('jobDescription.titleZA')}</option>
                        <option value="-updated_at">{t('jobDescription.recentlyUpdated')}</option>
                        <option value="updated_at">{t('jobDescription.leastRecentlyUpdated')}</option>
                      </select>
                    </div>
                  </div>

                  {hasActiveFilters() && (
                    <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-gray-200">
                      <span className="text-gray-600 font-medium text-xs">{t('jobDescription.activeFilters')}:</span>
                      {filters.search && (<span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs font-medium transition-all"><span>{t('jobDescription.search')} "{filters.search}"</span><button onClick={() => handleFilterChange("search", "")} className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"><X size={10} /></button></span>)}
                      {filters.jd_type && (<span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-xs font-medium transition-all"><span>{t('jobDescription.type')} {filters.jd_type}</span><button onClick={() => handleFilterChange("jd_type", "")} className="hover:bg-green-200 rounded-full p-0.5 transition-colors"><X size={10} /></button></span>)}
                      {filters.company && (<span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-800 rounded-full text-xs font-medium transition-all"><span>{t('jobDescription.company')}: {companies.find(c => c.id.toString() === filters.company)?.name}</span><button onClick={() => handleFilterChange("company", "")} className="hover:bg-orange-200 rounded-full p-0.5 transition-colors"><X size={10} /></button></span>)}
                      {filters.department && (<span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-100 text-indigo-800 rounded-full text-xs font-medium transition-all"><span>{t('jobDescription.dept')}: {departments.find(d => d.id.toString() === filters.department)?.name}</span><button onClick={() => handleFilterChange("department", "")} className="hover:bg-indigo-200 rounded-full p-0.5 transition-colors"><X size={10} /></button></span>)}
                      {filters.created_date && (<span className="inline-flex items-center gap-1 px-2 py-0.5 bg-pink-100 text-pink-800 rounded-full text-xs font-medium transition-all"><span>{t('jobDescription.created')} {filters.created_date}</span><button onClick={() => handleFilterChange("created_date", "")} className="hover:bg-pink-200 rounded-full p-0.5 transition-colors"><X size={10} /></button></span>)}
                      {filters.status && filters.status !== "open" && (<span className="inline-flex items-center gap-1 px-2 py-0.5 bg-cyan-100 text-cyan-800 rounded-full text-xs font-medium transition-all"><span>{t('jobDescription.status')}: {filters.status.charAt(0).toUpperCase() + filters.status.slice(1)}</span><button onClick={() => handleFilterChange("status", "open")} className="hover:bg-cyan-200 rounded-full p-0.5 transition-colors"><X size={10} /></button></span>)}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <CircularProgress size={48} sx={{ color: "#0ea5e9" }} />
                <p className="mt-4 text-gray-600 font-medium">{t('jobDescription.loadingJobDescriptions', 'Loading job descriptions...')}</p>
              </div>
            ) : jobDescriptions.length === 0 ? (
              <div className="text-center py-16">
                <div className="mx-auto mb-6 w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl flex items-center justify-center shadow-lg">
                  <Eye size={32} className="text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('jobDescription.noJobDescriptions')}</h3>
                <p className="text-gray-600 mb-6 text-lg">{hasActiveFilters() ? t('jobDescription.adjustFilters') : t('jobDescription.addFirstJD')}</p>
                {hasActiveFilters() && (
                  <button onClick={clearAllFilters} className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl">
                    {t('jobDescription.clearAllFilters')}
                  </button>
                )}
              </div>
            ) : (
              <div className={`rounded-2xl border-2 border-gray-200 shadow-lg ${expandedRows.size > 0 ? '' : 'overflow-x-auto'}`}>
                <table className="w-full table-fixed bg-white divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider w-[22%]">{t('jobDescription.tableTitle')}</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('jobDescription.matchingStatus')}</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('jobDescription.department')}</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('jobDescription.tableInterviewStyle')}</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('jobDescription.tablePublic')}</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">{t('jobDescription.tablePublicLink')}</th>
                      <th className="text-left py-3 px-2 text-xs font-medium text-gray-500 uppercase tracking-wider w-20">{t('jobDescription.tableUpdated')}</th>
                      <th className="text-center py-3 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-[264px]">{t('jobDescription.tableActions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {jobDescriptions.map((row, index) => (
                      <React.Fragment key={row.id}>
                        <tr className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 transition-colors`}>
                          <td className="py-2.5 px-4 align-middle">
                            <div
                              className="flex items-center gap-2 text-sm text-gray-900 hover:text-blue-600 cursor-pointer font-semibold transition-colors inline-block max-w-xs truncate"
                              onClick={() => handleOpenResumeMatching(row)}
                              title={`${row.title} - Created: ${formatDate(row.created_at)}`}
                            >
                              <span className="truncate">{truncateTitle(row.title, 50)}</span>
                              {(row.questions?.length || 0) > 0 && (
                                <span
                                  className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-[10px] font-semibold shrink-0"
                                  title={t('jobDescription.tooltips.questionCount', { count: row.questions.length })}
                                >
                                  {row.questions.length}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-2.5 px-4 align-middle">
                            {getStatusBadge(row.matching_status, t)}
                          </td>
                          <td className="py-2.5 px-4 align-middle">
                            <div className="text-sm text-gray-900">{row.department_name || "—"}</div>
                          </td>
                          <td className="py-2.5 px-4 align-middle">
                            <div className="flex flex-col gap-1.5">
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${row.interview_style === "campus_recruitment" ? "bg-purple-100 text-purple-800" : row.interview_style === "startup" ? "bg-orange-100 text-orange-800" : row.interview_style === "enterprise" ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-800"}`}>
                                {row.interview_style === "campus_recruitment" ? t('jobDescription.campus') : row.interview_style === "startup" ? t('jobDescription.startup') : row.interview_style === "enterprise" ? t('jobDescription.enterprise') : t('jobDescription.default')}
                              </span>
                              {row.interview_style === "campus_recruitment" && row.campus_questions_per_category && (
                                <span className="text-[10px] text-gray-500">{row.campus_questions_per_category} {t('jobDescription.form.perCategory', 'per Category')}</span>
                              )}
                            </div>
                          </td>
                          <td className="py-2.5 px-4 align-middle">
                            <div className="group relative inline-block">
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-150 ${row.is_public
                                ? "bg-green-100 text-green-800 group-hover:bg-green-200"
                                : "bg-yellow-100 text-yellow-800 group-hover:bg-yellow-200"
                                }`}>
                                {row.is_public ? t('jobDescription.public') : t('jobDescription.private')}
                              </span>
                              <div className="absolute left-0 top-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none group-hover:pointer-events-auto z-20">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleTogglePublicClick(row);
                                  }}
                                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md text-xs font-medium whitespace-nowrap shadow-md cursor-pointer transition-all"
                                  title={row.is_public ? t('jobDescription.makePrivate') : t('jobDescription.makePublic')}
                                >
                                  {row.is_public ? t('jobDescription.makePrivate') : t('jobDescription.makePublic')}
                                </button>
                              </div>
                            </div>
                          </td>
                          <td className="py-2.5 px-4 align-middle">
                            {row.is_public && row.public_link ? (
                              <div className="flex items-center gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm text-gray-600 font-mono truncate cursor-pointer hover:text-blue-600 transition-colors" title={row.public_link} onClick={() => window.open(row.public_link, "_blank")}>
                                    {truncateUrl(row.public_link, 25)}
                                  </div>
                                </div>
                                <button onClick={() => handleCopyPublicLink(row)} className={`p-1.5 rounded-md transition-all ${copiedId === row.id ? "text-green-600 bg-green-50" : "text-blue-600 hover:bg-blue-50"}`} title={t('jobDescription.copyPublicLink')}>
                                  {copiedId === row.id ? <Check size={14} /> : <Copy size={14} />}
                                </button>
                              </div>
                            ) : (
                              <div className="text-sm text-gray-400">{row.is_public ? t('jobDescription.noPublicLink') : t('jobDescription.notPublic')}</div>
                            )}
                          </td>
                          <td className="py-2.5 px-2 align-middle w-20">
                            <div
                              className="text-xs text-gray-500 cursor-help truncate"
                              title={formatFullDateTime(row.updated_at)}
                            >
                              {formatDate(row.updated_at)}
                            </div>
                          </td>
                          <td className="py-2.5 px-3 align-middle whitespace-nowrap">
                            <div className="inline-flex items-center justify-end gap-0.5 w-[264px]">
                              {row.matching_status?.toLowerCase() === 'processing' ? (
                                <div className="flex items-center justify-center gap-1 text-blue-600 font-medium text-[10px] px-2 py-1 bg-blue-50 rounded-lg animate-pulse whitespace-nowrap w-full">
                                  <Loader2 size={12} className="animate-spin" />
                                  {t('jobDescription.buttons.systemBusy')}
                                </div>
                              ) : (
                                <>
                                  <button type="button" onClick={() => { setDescription(row.description); setSelectedJobTitle(row.title); setOpenDescriptionDialog(true); }} className="flex items-center justify-center w-8 h-8 shrink-0 text-blue-600 hover:bg-blue-50 rounded-md transition-colors" title={t('jobDescription.viewDescription')}>
                                    <Eye size={14} />
                                  </button>
                                  <button type="button" onClick={() => handleOpenResumeMatching(row)} className="flex items-center justify-center w-8 h-8 shrink-0 text-purple-600 hover:bg-purple-50 rounded-md transition-colors" title={t('jobDescription.findMatchingResumes')}>
                                    <Users size={14} />
                                  </button>
                                  <button type="button" onClick={() => openEditJobDescription(row)} className="flex items-center justify-center w-8 h-8 shrink-0 text-blue-600 hover:bg-blue-50 rounded-md transition-colors" title={t('jobDescription.edit')}>
                                    <EditIcon fontSize="small" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedJobIdForTimeline(row.id);
                                      setTimelineOpen(true);
                                    }}
                                    className="flex items-center justify-center w-8 h-8 shrink-0 text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                                    title={t('jobDescription.viewTimeline')}
                                  >
                                    <Clock size={14} />
                                  </button>
                                  {filters.status === "archived" ? (
                                    <button type="button" onClick={() => handleUnarchive(row.id)} className="flex items-center justify-center w-8 h-8 shrink-0 text-green-600 hover:bg-green-50 rounded-md transition-colors" title={t('jobDescription.unarchive')}>
                                      <ArchiveRestore size={14} />
                                    </button>
                                  ) : (
                                    <button type="button" onClick={() => { setDeleteId(row.id); setDialogOpen(true); }} className="flex items-center justify-center w-8 h-8 shrink-0 text-red-600 hover:bg-red-50 rounded-md transition-colors" title={t('jobDescription.archive')}>
                                      <IoArchiveOutline fontSize="small" />
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => toggleRowExpansion(row.id)}
                                    className="flex items-center justify-center w-8 h-8 shrink-0 text-sky-600 hover:bg-sky-50 rounded-md transition-colors"
                                    title="Interview Benchmarks"
                                  >
                                    <ClipboardCheck size={14} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => toggleRowExpansion(row.id)}
                                    className="flex items-center justify-center w-8 h-8 shrink-0 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                                    title={t('jobDescription.viewDetails')}
                                  >
                                    {expandedRows.has(row.id) ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleAddQuestion(row.id)}
                                    className="flex items-center justify-center w-8 h-8 shrink-0 text-green-600 hover:bg-green-50 rounded-md transition-colors"
                                    title={t('jobDescription.buttons.addQuestions', 'Add Questions')}
                                  >
                                    <Plus size={14} />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                        <tr className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td colSpan={8} className="p-0">
                            <Collapse in={expandedRows.has(row.id)} timeout={0} unmountOnExit>
                              <div id={`jd-benchmarks-${row.id}`} className="max-w-full min-w-0">
                                <JobDescriptionCard jobDescription={row} onAddQuestion={handleAddQuestion} onUpdateQuestions={handleUpdateQuestions} onGenerateAiQuestion={handleGenerateAiQuestion} onAddFromQuestionBank={handleOpenQuestionBank} />
                              </div>
                            </Collapse>
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="sticky bottom-0 bg-white border-t">
              <Pagination currentPage={paginationData.current_page} totalPages={paginationData.total_pages} totalCount={paginationData.total_count} currentCount={jobDescriptions.length} onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange} pageSize={paginationData.page_size} showPageSize={false} hasActiveFilters={hasActiveFilters()} fixed={false} />
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AddJobDescriptionDialog
        open={showJobDescriptionModal}
        onClose={() => { setShowJobDescriptionModal(false); setSelectedJobDescription(null); setJobDescriptionForm(initialFormState); setJobDescriptionError(""); }}
        onSubmit={handleJobDescriptionSubmit}
        jobDescriptionForm={jobDescriptionForm}
        handleInputChange={(e) => setJobDescriptionForm(prev => ({ ...prev, [e.target.name]: e.target.value }))}
        departments={departments}
        selectedJobDescription={selectedJobDescription}
        jobDescriptionError={jobDescriptionError}
        setShowAddDepartmentModal={setShowAddDepartmentModal}
      />

      <AddDepartmentModal
        open={showAddDepartmentModal}
        onClose={() => { setShowAddDepartmentModal(false); setDepartmentError(""); setDepartmentSuccess(""); }}
        onSubmit={handleAddDepartment}
        departmentName={newDepartmentName}
        setDepartmentName={setNewDepartmentName}
        departmentError={departmentError}
        departmentSuccess={departmentSuccess}
      />

      {dialogOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4 z-[999999] animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl transform transition-all animate-in zoom-in-95 duration-200">
            <div className="p-8">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-red-100 to-red-200 rounded-2xl shadow-lg">
                <IoArchiveOutline className="text-red-600" size={28} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 text-center">{t('jobDescription.archiveJD')}</h3>
              <p className="text-gray-600 mb-8 text-center text-sm leading-relaxed">{t('jobDescription.archiveJDMessage')}</p>
              <div className="flex gap-4">
                <button
                  onClick={() => { setDialogOpen(false); setDeleteId(null); }}
                  className="flex-1 px-6 py-2.5 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 text-sm font-semibold shadow-sm hover:shadow-md"
                >
                  {t('jobDescription.cancel')}
                </button>
                <button
                  onClick={handleArchive}
                  className="flex-1 px-6 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl hover:from-red-700 hover:to-red-800 transition-all duration-200 text-sm font-semibold shadow-lg hover:shadow-xl"
                >
                  {t('jobDescription.archive')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showTogglePublicConfirm && jobToToggle && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4 z-[999999] animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl transform transition-all animate-in zoom-in-95 duration-200">
            <div className="p-8">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl shadow-lg">
                <Eye className="text-blue-600" size={28} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 text-center">
                {jobToToggle.is_public ? t('jobDescription.makePrivate') : t('jobDescription.makePublic')}
              </h3>
              <p className="text-gray-600 mb-8 text-center text-sm leading-relaxed">
                {jobToToggle.is_public
                  ? t('jobDescription.confirmMakePrivate')
                  : t('jobDescription.confirmMakePublic')}
              </p>
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setShowTogglePublicConfirm(false);
                    setJobToToggle(null);
                  }}
                  className="flex-1 px-6 py-2.5 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 text-sm font-semibold shadow-sm hover:shadow-md"
                >
                  {t('jobDescription.cancel')}
                </button>
                <button
                  onClick={confirmTogglePublic}
                  className="flex-1 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 text-sm font-semibold shadow-lg hover:shadow-xl"
                >
                  {jobToToggle.is_public ? t('jobDescription.makePrivate') : t('jobDescription.makePublic')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ResumeMatchingModal open={showResumeMatchingModal} onClose={() => { setShowResumeMatchingModal(false); setSelectedJobForMatching(null); }} onSubmit={handleStartResumeMatching} jobTitle={selectedJobForMatching?.title || ""} />

      {showAddQuestionModal && selectedJobForQuestion && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4 z-[999999] animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl transform transition-all animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-gray-200">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{t('jobDescription.addQuestionTitle')}</h3>
                  <p className="text-gray-600 mt-1 text-sm font-medium">{selectedJobForQuestion.title}</p>
                </div>
                <button
                  onClick={() => { setShowAddQuestionModal(false); setSelectedJobForQuestion(null); }}
                  className="p-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all duration-200"
                >
                  <X size={24} />
                </button>
              </div>
              <div className="space-y-6">
                {(selectedJobForQuestion.questions?.length || 0) === 0 && (
                  <div className="mb-6 space-y-3">
                    <div className="p-4 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl border-2 border-indigo-100 flex items-center justify-between shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-white rounded-xl text-indigo-600 shadow-sm border border-indigo-100">
                          <LibraryBooksOutlinedIcon sx={{ fontSize: 20 }} />
                        </div>
                        <div>
                          <div className="text-sm font-bold text-gray-900">{t('questionBank.addFromBank', 'Add from Question Bank')}</div>
                          <div className="text-xs text-gray-600 mt-0.5">{t('questionBank.subtitle', 'Create and reuse interview questions for your company')}</div>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setShowAddQuestionModal(false);
                          handleOpenQuestionBank(selectedJobForQuestion);
                        }}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-xs font-semibold hover:bg-indigo-700 transition-all shadow-md"
                      >
                        {t('questionBank.addFromBank', 'Add from Question Bank')}
                      </button>
                    </div>
                    <div className="mb-6 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border-2 border-blue-100 flex items-center justify-between shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 bg-white rounded-xl text-blue-600 shadow-sm border border-blue-100">
                        <AutoAwesomeOutlinedIcon sx={{ fontSize: 20 }} />
                      </div>
                      <div>
                        <div className="text-sm font-bold text-gray-900">{t('jobDescription.generateWithAI', 'Generate with AI')}</div>
                        <div className="text-xs text-gray-600 mt-0.5">{t('jobDescription.aiQuestionSubtitle', 'Use AI to generate tailored interview questions.')}</div>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setShowAddQuestionModal(false);
                        handleGenerateAiQuestion(selectedJobForQuestion);
                      }}
                      className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl text-xs font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg active:scale-95"
                    >
                      {t('jobDescription.generateQuestions', 'Generate Questions')}
                    </button>
                  </div>
                  </div>
                )}
                <div>
                  <label className="block text-gray-700 mb-3 font-semibold text-sm">{t('jobDescription.question')}</label>
                  <textarea
                    placeholder={t('jobDescription.questionPlaceholder')}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none transition-all bg-white shadow-sm hover:shadow-md text-sm"
                    rows="4"
                    value={globalQuestionText}
                    onChange={(e) => setGlobalQuestionText(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-3 font-semibold text-sm">{t('jobDescription.questionType')}</label>
                  <select
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white shadow-sm hover:shadow-md text-sm"
                    value={globalQuestionType}
                    onChange={(e) => setGlobalQuestionType(e.target.value)}
                  >
                    {questionTypeOptions.map((type) => (<option key={type.value} value={type.value}>{type.label}</option>))}
                  </select>
                </div>
                {globalQuestionType === "coding" && (
                  <div>
                    <label className="block text-gray-700 mb-3 font-semibold text-sm">{t('jobDescription.timeLimit', 'Time Limit (minutes)')}</label>
                    <input
                      type="number"
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white shadow-sm hover:shadow-md text-sm"
                      value={globalTimeLimit}
                      onChange={(e) => setGlobalTimeLimit(Math.min(60, Math.max(1, parseInt(e.target.value) || 5)))}
                      min="1"
                      max="60"
                    />
                    <p className="text-xs text-gray-500 mt-2">{t('jobDescription.timeLimitHelper', 'Default: 5 min (Max: 60)')}</p>
                  </div>
                )}
                {globalQuestionType === "mcq" && (
                  <div className="space-y-4 p-4 bg-gray-50 rounded-2xl border-2 border-gray-200">
                    <div className="text-sm text-gray-700 font-semibold">{t('jobDescription.multipleChoiceOptions')}</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {globalMcqOptions.map((opt, i) => (
                        <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-xl border-2 border-gray-200 hover:border-blue-300 transition-all">
                          <input
                            type="radio"
                            name="global-mcq-correct"
                            checked={globalMcqCorrectIndex === i}
                            onChange={() => setGlobalMcqCorrectIndex(i)}
                            aria-label={`Mark answer ${String.fromCharCode(65 + i)} correct`}
                            className="w-5 h-5 text-blue-600"
                          />
                          <input
                            type="text"
                            className="flex-1 p-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm"
                            placeholder={`${t('jobDescription.answer')} ${String.fromCharCode(65 + i)}`}
                            value={opt}
                            onChange={(e) => setGlobalMcqOptions(prev => prev.map((v, idx) => (idx === i ? e.target.value : v)))}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-4 pt-4 border-t-2 border-gray-200">
                  <button
                    onClick={() => { setShowAddQuestionModal(false); setSelectedJobForQuestion(null); setGlobalQuestionText(""); setGlobalQuestionType("concept"); setGlobalMcqOptions(["", "", "", ""]); setGlobalMcqCorrectIndex(0); }}
                    className="flex-1 px-6 py-2.5 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 text-sm font-semibold shadow-sm hover:shadow-md"
                  >
                    {t('jobDescription.cancel')}
                  </button>
                  <button
                    onClick={handleAddGlobalQuestion}
                    className="flex-1 px-6 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 transition-all duration-200 text-sm font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!globalQuestionText.trim() || (globalQuestionType === "mcq" && (globalMcqOptions.some(o => !o.trim()) || globalMcqCorrectIndex < 0 || globalMcqCorrectIndex > 3))}
                  >
                    {t('jobDescription.addQuestion')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <AIQuestionGeneratorModal
        open={showAiGeneratorInCard}
        onClose={() => {
          setShowAiGeneratorInCard(false);
          setSelectedJobForAiGenerator(null);
        }}
        onAddQuestions={handleAiQuestionsAddedInCard}
        jobTitle={selectedJobForAiGenerator?.title}
        experienceLevel={selectedJobForAiGenerator?.experience_level}
        requiredSkills={selectedJobForAiGenerator?.required_skills}
        niceToHaveSkills={selectedJobForAiGenerator?.nice_to_have_skills}
        existingQuestions={selectedJobForAiGenerator?.questions}
        companyId={departments.find((dep) => dep.id === selectedJobForAiGenerator?.department || String(dep.id) === String(selectedJobForAiGenerator?.department))?.company}
      />

      <QuestionBankPickerModal
        open={showQuestionBankPicker}
        onClose={() => {
          setShowQuestionBankPicker(false);
          setSelectedJobForBank(null);
        }}
        onAddQuestions={handleQuestionBankAdded}
        existingQuestions={(selectedJobForBank || selectedJobForQuestion)?.questions}
      />

      <ShowDescriptionDialog open={openDescriptionDialog} onClose={() => { setOpenDescriptionDialog(false); setSelectedJobTitle(""); }} content={parse(description)} title={selectedJobTitle} />

      <JDFromAudioModal
        open={showAudioModal}
        onClose={handleCloseAudioModal}
        onApply={handleAudioJDApply}
        initialAuditId={notificationAuditId}
      />

      <JDDraftsModal
        open={showDraftsModal}
        onClose={() => setShowDraftsModal(false)}
        onSelectDraft={handleSelectDraft}
      />

      {/* Timeline Drawer */}
      <Timeline
        open={timelineOpen}
        onClose={() => {
          setTimelineOpen(false);
          setSelectedJobIdForTimeline(null);
        }}
        apiEndpoint={selectedJobIdForTimeline
          ? `audit/timeline/job_descriptions/${selectedJobIdForTimeline}/`
          : "audit/timeline/job_descriptions/"}
        title={selectedJobIdForTimeline
          ? `${t('jobDescription.jobDescriptionsTimeline')} - ${jobDescriptions.find(j => j.id === selectedJobIdForTimeline)?.title || 'ID: ' + selectedJobIdForTimeline}`
          : t('jobDescription.jobDescriptionsTimeline')}
        pageName={t('jobDescription.title')}
        width={450}
      />

      {/* Fixed Vertical Timeline Button - Center Right/Left based on RTL */}
      <button
        onClick={() => setTimelineOpen(true)}
        className={`fixed ${isRTL ? 'left-4' : 'right-4'} top-1/2 -translate-y-1/2 z-40 flex items-center justify-center text-white p-2 rounded-full transition-all duration-200 group hover:scale-110`}
        style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #ec4899 100%)',
          boxShadow: '0 3px 6px rgba(0, 0, 0, 0.15)'
        }}
      >
        <Clock size={14} />
        <span className={`absolute ${isRTL ? 'left-full ml-3' : 'right-full mr-3'} px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none`}>
          {t('jobDescription.timeline')}
        </span>
      </button>
    </div>
  );
}
